import ArrowDownBottomLine from '@assets/icons/arrow-down-bottom-line.svg';
import ClockIcon from '@assets/icons/clock.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { AvailabilityConstraints } from '@/experiences/sales-portal/domain/ordering/blocs/loaders/use-sales-portal-available-order-fulfillments-loader';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { ChangeFulfillmentTrigger } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/change-fulfillment-trigger';
import { CheckoutPanelContent } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/checkout-panel-content';
import { createStyles } from '@/contexts/theme-context';
import { GetAvailableOrderFulfillmentResponse } from '@/types';
import { InputValueMap } from '@/utils/new-order';
import {
  ModalSheet,
  ScrollView,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { OrderEntryCheckoutSummaryEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-checkout-summary-empty-state';
import { OrderEntryCheckoutSummaryErrorState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-checkout-summary-error-state';
import { OrderEntryMobileFulfillmentPushView } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-fulfillment-push-view';
import { OrderEntryMobileOrderPlaced } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-order-placed';
import { OrderSummaryAlerts } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/order-summary-alerts';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { useCartStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { UseFulfillmentSelectionReturn } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { useOrderEntryCheckoutSummary } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-checkout-summary';
import { useOrderEntryMobileFulfillmentPicker } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-mobile-fulfillment-picker';
import { useResetFulfillmentOnOrderSubmission } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-reset-fulfillment-on-order-submission';
import { useSalesPortalCheckoutAdditionalInputs } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-sales-portal-checkout-additional-inputs';
import { useSalesPortalPoNumberDisplayMode } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-po-number-display-mode';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';
import { UseSaveOrderFulfillmentSettingsOptions } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-save-order-fulfillment-settings';
import { useSubmitOrderMutator } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/mutators/use-submit-order-mutator';
import { useSupplierDynamicConfigLoader } from '@/experiences/sales-portal/blocs/loaders/use-supplier-dynamic-config-loader';

const CONSTRAINT_ICON_SIZE = 16;
const TOTAL_SKELETON_BORDER_RADIUS = 20;
const TOTAL_SKELETON_HEIGHT = 18;
const TOTAL_SKELETON_WIDTH = 80;

interface OrderEntryMobileCheckoutSheetProps {
  availabilityConstraints: AvailabilityConstraints | null;
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  commissionDisplayValue?: string | null;
  fulfillmentSelection: UseFulfillmentSelectionReturn;
  isGrossProfitHidden: boolean;
  onClose: () => void;
  onOrderPlaced: () => void;
  onSaveFulfillment: (
    fulfillment: SavedSelectedFulfillment,
    options?: UseSaveOrderFulfillmentSettingsOptions,
  ) => Promise<void>;
  open: boolean;
  refetchCommission?: (() => void) | null;
  restaurantUUID: string;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  selectedRestaurant: RestaurantListData;
  supplierUUID: string;
  timezones: TimeZoneMap;
}

export const OrderEntryMobileCheckoutSheet: React.FC<
  OrderEntryMobileCheckoutSheetProps
> = ({
  availabilityConstraints,
  availableFulfillments,
  commissionDisplayValue,
  fulfillmentSelection,
  isGrossProfitHidden,
  onClose,
  onOrderPlaced,
  onSaveFulfillment,
  open,
  refetchCommission,
  restaurantUUID,
  savedSelectedFulfillment,
  selectedRestaurant,
  supplierUUID,
  timezones,
}) => {
  const [styles, theme] = useStyles();
  const { showErrorToast } = useExperienceToasts();

  const {
    canPlaceOrder,
    firstSummary,
    hasError,
    isLoading,
    isSummaryEmpty,
    orderUUID,
    orderValidation,
    splitOrderCount,
    splitOrderMetricRows,
    summarizeOrder,
  } = useOrderEntryCheckoutSummary({
    commissionDisplayValue,
    isGrossProfitHidden,
    open,
    refetchCommission,
    restaurantUUID,
    supplierUUID,
  });

  const [additionalInputValueMap, setAdditionalInputValueMap] =
    useState<InputValueMap>({});
  const { supplierDynamicConfig, loading: supplierDynamicConfigLoading } =
    useSupplierDynamicConfigLoader({ supplierUUID });
  const { resetFulfillmentOnOrderSubmission } =
    useResetFulfillmentOnOrderSubmission({
      availableFulfillments,
      chatUUID: selectedRestaurant.chatUUID,
      restaurantUUID,
      supplierUUID,
      timezones,
    });
  const supplierMetadata = useSalesPortalSupplierMetadataLoader({
    supplierUUID,
  });
  const {
    error: poNumberDisplayModeError,
    loading: poNumberDisplayModeLoading,
    poNumberDisplayMode,
    refetch: refetchPoNumberDisplayMode,
    refetching: refetchingPoNumberDisplayMode,
  } = useSalesPortalPoNumberDisplayMode({
    restaurantUUID,
    supplierUUID,
  });
  const showPoNumberDisplayModeError =
    !!poNumberDisplayModeError || refetchingPoNumberDisplayMode;
  const {
    additionalInputErrorMap,
    additionalInputs,
    areAdditionalInputsValid,
    clearAdditionalInputError,
    clearAllAdditionalInputError,
    validateAdditionalInput,
  } = useSalesPortalCheckoutAdditionalInputs(
    supplierDynamicConfig?.order_summary_config,
    poNumberDisplayMode,
    supplierMetadata.showOrderNotes,
    additionalInputValueMap,
  );

  const handleAdditionalInputChange = useCallback(
    (id: string, title: string, value: string) => {
      setAdditionalInputValueMap((previous) => ({
        ...previous,
        [id]: { title, value },
      }));
    },
    [],
  );

  const { submitOrder, loading: isSubmittingOrder } = useSubmitOrderMutator();
  const [isOrderPlaced, setIsOrderPlaced] = useState(false);

  const handleSaveSelectedFulfillment = useCallback(
    (fulfillment: SavedSelectedFulfillment) =>
      onSaveFulfillment(fulfillment, {
        onError: summarizeOrder,
        onSuccess: summarizeOrder,
      }),
    [onSaveFulfillment, summarizeOrder],
  );
  const {
    closePicker: closeFulfillmentPicker,
    isPickerOpen: isFulfillmentPickerOpen,
    openPicker: openFulfillmentPicker,
    shouldRenderPicker: shouldRenderFulfillmentPicker,
  } = useOrderEntryMobileFulfillmentPicker();

  useEffect(() => {
    if (!open) {
      setIsOrderPlaced(false);
      closeFulfillmentPicker();
    }
  }, [closeFulfillmentPicker, open]);

  const isPlaceOrderDisabled =
    isLoading ||
    !canPlaceOrder ||
    isSubmittingOrder ||
    supplierDynamicConfigLoading ||
    poNumberDisplayModeLoading ||
    showPoNumberDisplayModeError ||
    !areAdditionalInputsValid;

  const handlePlaceOrder = useCallback(async () => {
    if (!orderUUID || !areAdditionalInputsValid) {
      return;
    }

    try {
      const result = await submitOrder(orderUUID, additionalInputValueMap);
      if (!result?.data?.submitOrder?.order_id) {
        onClose();
        showErrorToast(
          result?.errors?.[0]?.message ??
            i18n.t('sales_portal.order_entry.checkout_panel.place_order_error'),
        );
        return;
      }

      setAdditionalInputValueMap({});
      clearAllAdditionalInputError();
      useCartStore.getState().clearCart();
      resetFulfillmentOnOrderSubmission();
      setIsOrderPlaced(true);
      onOrderPlaced();
    } catch (error) {
      onClose();
      showErrorToast(
        error instanceof Error
          ? error.message
          : i18n.t('sales_portal.order_entry.checkout_panel.place_order_error'),
      );
    }
  }, [
    additionalInputValueMap,
    areAdditionalInputsValid,
    clearAllAdditionalInputError,
    onClose,
    onOrderPlaced,
    orderUUID,
    resetFulfillmentOnOrderSubmission,
    showErrorToast,
    submitOrder,
  ]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  const cutoffTimeString = availabilityConstraints?.cutoffTimeString;
  const orderMinimumDisplayString =
    availabilityConstraints?.orderMinimumDisplayString;
  const { selectedFulfillmentDisplay } = fulfillmentSelection;

  const showFallbackState =
    !isLoading && (hasError || showPoNumberDisplayModeError || isSummaryEmpty);
  const placeOrderLabel =
    splitOrderCount <= 1
      ? i18n.t('sales_portal.order_entry.checkout_panel.place_order')
      : i18n.t('sales_portal.order_entry.checkout_panel.place_orders', {
          count: splitOrderCount,
        });

  return (
    <>
      <ModalSheet
        aria-label={i18n.t('sales_portal.bottom_bar.checkout')}
        onOpenChange={handleOpenChange}
        open={open}
      >
        <ModalSheet.Header closeLabel={i18n.t('close')} />
        <ModalSheet.Body>
          {/* TODO(DSR-4181): present success as its own fitted sheet over the list once sk-ui ships one (DSR-4180). */}
          {isOrderPlaced ? (
            <OrderEntryMobileOrderPlaced
              restaurantName={selectedRestaurant.restaurantName}
            />
          ) : (
            <ScrollView
              automaticallyAdjustKeyboardInsets
              contentContainerStyle={
                showFallbackState
                  ? styles.scrollContentCentered
                  : styles.scrollContent
              }
              keyboardShouldPersistTaps="handled"
            >
              <YStack gap="$4" marginBottom="$24">
                <Typography size="$lg" variant="heading">
                  {selectedRestaurant.restaurantName}
                </Typography>
                <Typography color={theme.sk.color.text.tertiary} size="$md">
                  {selectedRestaurant.restaurantAddress}
                </Typography>
                <XStack gap="$16" marginTop="$4">
                  {orderMinimumDisplayString ? (
                    <XStack alignItems="center" gap="$4">
                      <ArrowDownBottomLine
                        fill={theme.sk.color.icon.tertiary}
                      />
                      <Typography
                        color={theme.sk.color.text.tertiary}
                        size="$md"
                      >
                        {`${i18n.t('sales_portal.fulfillment_settings.minimum')} ${orderMinimumDisplayString}`}
                      </Typography>
                    </XStack>
                  ) : null}
                  {cutoffTimeString ? (
                    <XStack alignItems="center" gap="$4">
                      <ClockIcon
                        height={CONSTRAINT_ICON_SIZE}
                        stroke={theme.sk.color.icon.tertiary}
                        width={CONSTRAINT_ICON_SIZE}
                      />
                      <Typography
                        color={theme.sk.color.text.tertiary}
                        size="$md"
                      >
                        {`${i18n.t('sales_portal.fulfillment_settings.cutoff')} ${cutoffTimeString}`}
                      </Typography>
                    </XStack>
                  ) : null}
                </XStack>
              </YStack>
              {showFallbackState ? (
                <>
                  {hasError ? null : (
                    <OrderSummaryAlerts orderValidation={orderValidation} />
                  )}
                  <YStack flex={1} justifyContent="center">
                    {hasError ? (
                      <OrderEntryCheckoutSummaryErrorState
                        onPress={summarizeOrder}
                      />
                    ) : showPoNumberDisplayModeError ? (
                      <OrderEntryCheckoutSummaryErrorState
                        description={i18n.t(
                          'sales_portal.checkout_panel.po_setting.error.description',
                        )}
                        loading={refetchingPoNumberDisplayMode}
                        onPress={() => refetchPoNumberDisplayMode()}
                        title={i18n.t(
                          'sales_portal.checkout_panel.po_setting.error.title',
                        )}
                      />
                    ) : (
                      <OrderEntryCheckoutSummaryEmptyState onPress={onClose} />
                    )}
                  </YStack>
                </>
              ) : (
                <CheckoutPanelContent
                  additionalInputErrorMap={additionalInputErrorMap}
                  additionalInputs={additionalInputs}
                  additionalInputValueMap={additionalInputValueMap}
                  firstOrderAction={
                    savedSelectedFulfillment ? (
                      <ChangeFulfillmentTrigger
                        disabled={isLoading || isSubmittingOrder}
                        onPress={openFulfillmentPicker}
                      />
                    ) : undefined
                  }
                  isLoading={isLoading}
                  onAdditionalInputBlur={validateAdditionalInput}
                  onAdditionalInputChange={handleAdditionalInputChange}
                  onAdditionalInputClearError={clearAdditionalInputError}
                  orderEntrySummary={firstSummary}
                  orderValidation={orderValidation}
                  savedSelectedFulfillment={savedSelectedFulfillment}
                  selectedFulfillmentDisplay={selectedFulfillmentDisplay}
                  splitOrderMetricRows={splitOrderMetricRows}
                  timezones={timezones}
                />
              )}
            </ScrollView>
          )}
        </ModalSheet.Body>
        {isOrderPlaced ? (
          <ModalSheet.Footer>
            <YStack flex={1}>
              <Button
                accessibilityLabel={i18n.t(
                  'sales_portal.order_entry.order_placed_modal.cta',
                )}
                onPress={onClose}
                size={ButtonSize.Medium}
                variant={ButtonVariant.Primary}
                width="100%"
              >
                {i18n.t('sales_portal.order_entry.order_placed_modal.cta')}
              </Button>
            </YStack>
          </ModalSheet.Footer>
        ) : !showFallbackState ? (
          <ModalSheet.Footer>
            <YStack flex={1} justifyContent="center">
              <Typography color={theme.sk.color.text.secondary} size="$sm">
                {i18n.t('total')}
              </Typography>
              {isLoading ? (
                <AnimatedSkeletonLoader
                  borderRadius={TOTAL_SKELETON_BORDER_RADIUS}
                  height={TOTAL_SKELETON_HEIGHT}
                  width={TOTAL_SKELETON_WIDTH}
                />
              ) : (
                <Typography size="$sm" variant="heading">
                  {firstSummary?.orderFees.total?.amountDisplayString ?? '—'}
                </Typography>
              )}
            </YStack>
            <Button
              accessibilityLabel={placeOrderLabel}
              disabled={isPlaceOrderDisabled}
              loading={isSubmittingOrder}
              onPress={handlePlaceOrder}
              size={ButtonSize.Medium}
              style={styles.placeOrderButton}
              variant={ButtonVariant.Primary}
            >
              {placeOrderLabel}
            </Button>
          </ModalSheet.Footer>
        ) : null}
      </ModalSheet>
      {savedSelectedFulfillment && shouldRenderFulfillmentPicker ? (
        <OrderEntryMobileFulfillmentPushView
          availableFulfillments={availableFulfillments}
          createIsDateDisabled={fulfillmentSelection.createIsDateDisabled}
          getTimeslotCategories={fulfillmentSelection.getTimeslotCategories}
          onClose={closeFulfillmentPicker}
          onSave={handleSaveSelectedFulfillment}
          open={isFulfillmentPickerOpen}
          savedSelectedFulfillment={savedSelectedFulfillment}
          timezones={timezones}
          validateFulfillmentDate={fulfillmentSelection.validateFulfillmentDate}
          validateTimeslot={fulfillmentSelection.validateTimeslot}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  placeOrderButton: {
    paddingHorizontal: theme.sk.spacing.spaceXlarge,
  },
  scrollContent: {
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
  scrollContentCentered: {
    flexGrow: 1,
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
}));
